import { useState, useEffect } from "react"

import { GetAddressesResponse } from "@lib/clients/nelo-api-client"
import { medusaClient } from "@lib/config"

export interface ProductTotalBreakdown {
  shippingTaxTotal?: number
  shippingTotal?: number
  taxTotal?: number
  subTotal?: number
  total?: number
  discountTotal?: number
}

export interface ProductDetails {
  thumbnail: string | null
  name: string
}

export interface CartDetails {
  productTotalBreakdown: ProductTotalBreakdown
  total: number
  productDetails: ProductDetails
}

const formatAmount = (amount: number | undefined): number => {
  return amount !== undefined ? Number((amount / 100).toFixed(2)) : 0
}

export const useCartCreation = (
  variantId: string | null,
  isInitialized: boolean,
  addresses: GetAddressesResponse | null,
  isAddressSet: boolean
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [cartId, setCartId] = useState<string | null>(null)
  const [cartTotal, setCartTotal] = useState<number | null>(null)
  const [cartDetails, setCartDetails] = useState<CartDetails | null>(null)
  const [isShippingMethodSet, setIsShippingMethodSet] = useState<boolean>(false)
  const [isPaymentSessionCreated, setIsPaymentSessionCreated] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (variantId && isInitialized && addresses && isAddressSet) {
      const createCart = async () => {
        setIsLoading(true)
        setError(null)
        const data = {
          items: [{ variant_id: variantId, quantity: 1 }],
          country_code: "MX",
          region_id: "reg_01HN3ZV7SDC7WP0GGV1GWWJ500",
        }

        try {
          const createdCart = await medusaClient.carts.create(data)
          setCartId(createdCart.cart.id)

          const addressWrapper = addresses.addresses.find((wrapper) => wrapper.address.predetermined) || addresses.addresses[0]
          const address = addressWrapper.address
          const splitName = address.addressedTo?.split(" ") || ["", ""]

          //set the cart address
          await medusaClient.carts.update(createdCart.cart.id, {
            shipping_address: {
              postal_code: address.postalCode,
              province: address.colony,
              country_code: "mx",
              city: address.city,
              address_1: `${address.buildingNumber || ""} ${address.street || ""}`.trim(),
              address_2: address.interiorNumber || "",
              first_name: splitName[0],
              last_name: splitName.slice(1).join(" "),
              metadata: {
                instructions: address.deliveryInstructions,
                reference: address.reference,
              },
            },
          })

          const { shipping_options } = await medusaClient.shippingOptions.listCartOptions(createdCart.cart.id)

          if (shipping_options.length === 0) {
            console.log(`failed to fetch shipping options for cart ${cartId}`)
          }

          if (shipping_options[0]?.id) {
            await medusaClient.carts.addShippingMethod(createdCart.cart.id, {
              option_id: shipping_options[0].id,
            })
            setIsShippingMethodSet(true)
          } else {
            throw new Error("No shipping options available")
          }

          await medusaClient.carts.createPaymentSessions(createdCart.cart.id)

          await medusaClient.carts.setPaymentSession(createdCart.cart.id, {
            provider_id: "nelo",
          })

          setIsPaymentSessionCreated(true)

          const { cart } = await medusaClient.carts.retrieve(createdCart.cart.id)

          const productTotalBreakdown: ProductTotalBreakdown = {
            shippingTaxTotal: formatAmount(cart.shipping_tax_total!),
            shippingTotal: formatAmount(cart.shipping_total),
            taxTotal: formatAmount(cart.tax_total!),
            subTotal: formatAmount(cart.subtotal),
            total: formatAmount(cart.total),
            discountTotal: formatAmount(cart.discount_total),
          }

          const productDetails: ProductDetails = {
            thumbnail: cart.items[0].thumbnail,
            name: cart.items[0].title,
          }

          const total = cart.total!

          const newCartDetails: CartDetails = {
            productTotalBreakdown,
            total,
            productDetails,
          }

          setCartDetails(newCartDetails)
        } catch (err) {
          setError(err instanceof Error ? err : new Error("An unknown error occurred"))
          console.error("Failed to create cart:", err)
        } finally {
          setIsLoading(false)
        }
      }
      createCart()
    }
  }, [variantId, isInitialized, addresses, isAddressSet])

  return { isLoading, cartId, cartTotal, cartDetails, isPaymentSessionCreated, isShippingMethodSet, error }
}
